import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { injectIntl } from 'react-intl';
import { createForm } from 'rc-form';
import { Switch, Redirect } from 'react-router-dom';
import { Grid, Typography } from '@material-ui/core';

import { DEFAULT_ROLE_ORG_ADMIN } from 'yeself-common/defaults/ApplicationDefaults';

import { makeSelectIsRoleIncluded } from 'yeself-client-common/security/SecuritySelector';
import { selectFeatureFlags } from 'yeself-client-common/featureFlags/FeatureFlagsSelector';

import PrivateRoute from 'yeself-client-common/security/components/PrivateRoute';

import { selectApplicationName } from 'Console/data/applicationDetail/settings/SettingsSelector';

import NavSider from 'Console/components/applications/detail/settings/NavSider';
import Breadcrumb from 'Console/components/common/Breadcrumb';
import General from 'Console/components/applications/detail/settings/general/General';
import Integration from 'Console/components/applications/detail/settings/integration/Integration';
import Language from 'Console/components/applications/detail/settings/language/Language';
import Identity from 'Console/settings/identity/components/Identity';
import Multitenancy from 'Console/components/applications/detail/settings/multitenancy/Multitenancy';
import Tracking from 'Console/components/applications/detail/settings/tracking/Tracking';
import GuideDefaults from 'Console/components/applications/detail/settings/guideDefaults/GuideDefaults';
import Access from 'Console/components/applications/detail/settings/access/Access';
import SelectorSystem from 'Console/components/applications/detail/settings/selectorSystem/SelectorSystem';
import Confusion from 'Console/components/applications/detail/settings/confusion/Confusion';
import AddOns from 'Console/components/applications/detail/settings/addOns/AddOns';
import Audience from 'Console/components/applications/detail/settings/audience/Audience';
import Theme from 'Console/components/applications/detail/settings/theme/Theme';
import SettingsNotificationContainer from 'Console/data/applicationDetail/settings/components/SettingsNotificationContainer';
import SelectorDefaults from 'Console/components/applications/detail/settings/selectorDefaults/SelectorDefaults';

import { ROUTES } from 'Console/constants/routes';

const styles = (theme) => ({
    root: {
        flexGrow: 1,
        alignItems: 'center',
        padding: '0 24px',
    },
    navLink: {
        textDecoration: 'none',
    },
    paper: {
        padding: theme.spacing.unit * 2,
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    navList: {
        [theme.breakpoints.up('lg')]: {
            maxWidth: '18%',
        },
        [theme.breakpoints.up('xl')]: {
            maxWidth: '15%',
        },
        [theme.breakpoints.down('md')]: {
            marginBottom: '20px',
        },
    },
    formBox: {
        [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
        },
        [theme.breakpoints.up('md')]: {
            maxWidth: '83%',
        },
        [theme.breakpoints.up('lg')]: {
            maxWidth: '82%',
        },
        [theme.breakpoints.up('xl')]: {
            maxWidth: '85%',
        },
    },
});

const ApplicationSettings = ({ classes, intl, hasGlobalRoutePrefix = false, form, match }) => {
    const routePrefix = hasGlobalRoutePrefix ? ROUTES.SETTINGS : match.url;

    const selectUserRole = useMemo(makeSelectIsRoleIncluded, []);

    const name = useSelector(selectApplicationName);
    const featureFlags = useSelector(selectFeatureFlags);
    const isRoleOrganizationAdmin = useSelector((state) =>
        selectUserRole(state, {
            includedRole: DEFAULT_ROLE_ORG_ADMIN,
        })
    );

    return (
        <>
            <SettingsNotificationContainer />
            <Grid container className={classes.root}>
                <Switch>
                    <>
                        <Grid container spacing={24} justify="center" alignItems="center">
                            <Grid item xs={12} md={hasGlobalRoutePrefix ? 10 : 12}>
                                <Breadcrumb
                                    paths={[
                                        {
                                            to: ROUTES.APPLICATIONS,
                                            text: intl.formatMessage({ id: 'Navigation.applications' }),
                                        },
                                        {
                                            text: name,
                                        },
                                    ]}
                                />
                                <Typography variant="h6">
                                    {intl.formatMessage({ id: 'Navigation.settings' })}
                                </Typography>
                            </Grid>
                            <Grid item container xs={12} md={hasGlobalRoutePrefix ? 10 : 12}>
                                <Grid item xs={12} md={2} className={classes.navList}>
                                    <NavSider
                                        isRoleOrganizationAdmin={isRoleOrganizationAdmin}
                                        intl={intl}
                                        routePrefix={routePrefix}
                                        featureFlags={featureFlags}
                                    />
                                </Grid>

                                <Grid item xs={12} className={classes.formBox}>
                                    <Switch>
                                        <PrivateRoute
                                            path={`${routePrefix}${ROUTES.SETTINGS_GENERAL}`}
                                            component={General}
                                            form={form}
                                            match={match}
                                        />
                                        <PrivateRoute
                                            path={`${routePrefix}${ROUTES.SETTINGS_INTEGRATION}`}
                                            component={Integration}
                                            match={match}
                                        />
                                        <PrivateRoute
                                            path={`${routePrefix}${ROUTES.SETTINGS_LANGUAGE}`}
                                            component={Language}
                                            form={form}
                                            match={match}
                                        />
                                        <PrivateRoute
                                            path={`${routePrefix}${ROUTES.SETTINGS_IDENTITY}`}
                                            component={Identity}
                                            form={form}
                                            match={match}
                                        />
                                        <PrivateRoute
                                            path={`${routePrefix}${ROUTES.SETTINGS_MULTITENANCY}`}
                                            component={Multitenancy}
                                            form={form}
                                            match={match}
                                        />
                                        <PrivateRoute
                                            path={`${routePrefix}${ROUTES.SETTINGS_TRACKING}`}
                                            component={Tracking}
                                            form={form}
                                            match={match}
                                        />
                                        <PrivateRoute
                                            path={`${routePrefix}${ROUTES.SETTINGS_CONFUSION}`}
                                            component={Confusion}
                                            form={form}
                                            match={match}
                                        />
                                        <PrivateRoute
                                            path={`${routePrefix}${ROUTES.SETTINGS_GUIDE_DEFAULTS}`}
                                            component={GuideDefaults}
                                            form={form}
                                            match={match}
                                        />
                                        {isRoleOrganizationAdmin && (
                                            <PrivateRoute
                                                path={`${routePrefix}${ROUTES.SETTINGS_ACCESS}`}
                                                component={Access}
                                                form={form}
                                                match={match}
                                            />
                                        )}
                                        <PrivateRoute
                                            path={`${routePrefix}${ROUTES.SETTINGS_SELECTOR_SYSTEM}`}
                                            component={SelectorSystem}
                                            form={form}
                                            match={match}
                                        />
                                        <PrivateRoute
                                            path={`${routePrefix}${ROUTES.SETTINGS_SELECTOR_DEFAULTS}`}
                                            component={SelectorDefaults}
                                            match={match}
                                        />
                                        <PrivateRoute
                                            path={`${routePrefix}${ROUTES.SETTINGS_ADD_ONS}`}
                                            component={AddOns}
                                            form={form}
                                            match={match}
                                        />
                                        <PrivateRoute
                                            path={`${routePrefix}${ROUTES.SETTINGS_AUDIENCE}`}
                                            component={Audience}
                                            form={form}
                                            match={match}
                                        />
                                        <PrivateRoute
                                            path={`${routePrefix}${ROUTES.SETTINGS_THEME}`}
                                            component={Theme}
                                            form={form}
                                            match={match}
                                        />
                                        <Redirect
                                            from={match.url}
                                            to={`${routePrefix}${ROUTES.SETTINGS_GENERAL}`}
                                        />
                                    </Switch>
                                </Grid>
                            </Grid>
                        </Grid>
                    </>
                </Switch>
            </Grid>
        </>
    );
};

export default withStyles(styles)(injectIntl(createForm()(ApplicationSettings)));
